import gql from 'graphql-tag'
import React, { useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, BoxProps, Flex, FlexProps, Text } from 'rebass'
import { useMutation } from '@apollo/react-hooks'
import { styled } from '../../../styles/settings/theme'

import { Game, Team } from '../../../types/graphql'
import { rawNumPlayers } from '../../../utils/sportUtils'
import { numToString } from '../../../utils/typeCoercions'
import { BaseButton } from '../../atoms/Buttons'
import LoadingSpinner from '../../atoms/LoadingSpinner'
import SportLogo from '../../atoms/SportLogo'
import FortniteReportingTable from './FortniteReportingTable'
import { GameBoxReportEvidence, useEvidence } from './evidence'
import { currentUserIsAdmin } from '../../../utils/admins'

export const REPORT_FORTNITE_GAME_MUTATION = gql`
  mutation report_fortnite_game(
    $gameId: ID!
    $winningTeamId: ID!
    $homeTeam: UserReportedGame!
    $awayTeam: UserReportedGame!
    $evidenceScreenshot: String!
  ) {
    reportFortniteGame(
      gameId: $gameId
      winningTeamId: $winningTeamId
      homeTeam: $homeTeam
      awayTeam: $awayTeam
      evidenceScreenshot: $evidenceScreenshot
    ) {
      match {
        id
        settled
      }
    }
  }
`

interface GameProps {
  game: Game
  homeTeam: Team
  awayTeam: Team
  toOpen: any
  updateLoading: (loading: boolean) => void
  report: number
  index: number
  reportable: boolean
  settled: boolean
}
const Circle = styled(Box)<BoxProps>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
`
const RowBox = styled(Flex)<FlexProps>`
  flex-direction: column;
  border: 2px solid ${props => props.theme.colors.darkmiddlegray};
  padding: 2rem 1.5rem;
  margin-top: 1.25rem;
`
const Reporting = styled(Flex)<FlexProps>`
  border-top: 2px solid ${props => props.theme.colors.lightgray};
`
interface Action {
  payload: { index: number; name: string; kills: number }
}

const teamReducer = (state: any, action: Action) => {
  return {
    ...state,
    [action.payload.index]: { name: action.payload.name, kills: action.payload.kills },
  }
}

const FortniteGameBox = ({
  game,
  homeTeam,
  awayTeam,
  toOpen,
  report,
  index,
  reportable,
  updateLoading,
  settled,
}: GameProps) => {
  const [ready, setReady] = useState(false)
  const { evidenceScreenshotUrl, evidenceScreenshotName, handleEvidenceChanged } = useEvidence()

  const activeBox = report === index

  const [reportFortniteGame, { loading }] = useMutation(REPORT_FORTNITE_GAME_MUTATION, {
    refetchQueries: ['matchDetailPage'],
    onError(error) {},
    onCompleted(data) {
      if (data.reportFortniteGame.match.settled) {
        toast.success(
          "Thank you for your submission. We're processing it now and brackets will be updated within 5 minutes",
          { containerId: 'temporary' },
        )
      } else {
        toast.success('Thank you for your submission!', { containerId: 'temporary' })
      }
      changeOpenIndex(-1)
      updateLoading(false)
    },
  })

  const num = Number(rawNumPlayers(homeTeam.sportSlug))

  //create initial state object
  const results = (team: any) => {
    return {
      ...[...Array(num)].map((el, i) => {
        return {
          name: team.activeRosterEntries[i]
            ? team.activeRosterEntries[i].player.username
            : team.activeRosterEntries[0].player.username,
          kills: 0,
        }
      }),
    }
  }

  const homeTeamInitialState = results(homeTeam)
  const awayTeamInitialState = results(awayTeam)

  const [homeTeamState, homeDispatch] = useReducer(teamReducer, homeTeamInitialState)
  const [awayTeamState, awayDispatch] = useReducer(teamReducer, awayTeamInitialState)

  const homeTeamScore = Object.values(homeTeamState)
    .map((player: any) => player.kills)
    .reduce((a: number, b: number) => a + b)

  const awayTeamScore = Object.values(awayTeamState)
    .map((player: any) => player.kills)
    .reduce((a: number, b: number) => a + b)

  useEffect(() => {
    const isReadyForSubmit = () => {
      const someoneWon =
        (homeTeamScore && homeTeamScore > 0) || (awayTeamScore && awayTeamScore > 0)
      return someoneWon && evidenceScreenshotUrl
    }
    setReady(isReadyForSubmit())

    if (activeBox) {
      loading ? updateLoading(true) : updateLoading(false)
    }
  }, [homeTeamScore, awayTeamScore, activeBox, loading, updateLoading, evidenceScreenshotUrl])

  const handleNameChange = (e: any, isHomeTeam: any, i: any) => {
    isHomeTeam
      ? homeDispatch({
          payload: { index: i, name: e.target.value, kills: homeTeamState[i].kills },
        })
      : awayDispatch({
          payload: { index: i, name: e.target.value, kills: awayTeamState[i].kills },
        })
  }

  const handleKillsChange = (e: any, isHomeTeam: any, i: any) => {
    isHomeTeam
      ? homeDispatch({
          payload: { index: i, name: homeTeamState[i].name, kills: e.target.value },
        })
      : awayDispatch({
          payload: { index: i, name: awayTeamState[i].name, kills: e.target.value },
        })
  }

  const playerLookupId =
    homeTeam && homeTeam.activeRosterEntries && awayTeam && awayTeam.activeRosterEntries
      ? homeTeam.activeRosterEntries
          .concat(awayTeam.activeRosterEntries)
          .map((entry: any) => {
            return { name: entry.player.username, id: entry.player.id }
          })
          .reduce((obj: any, item: any) => {
            obj[item.name] = item.id
            return obj
          }, {})
      : null

  const changeOpenIndex = (index: number) => {
    toOpen(index)
  }

  const checkData = () => {
    //checks for duplicate names
    const homeNameObj: { [key: string]: boolean } = {}
    for (let key in homeTeamState) {
      if (!homeNameObj[homeTeamState[key].name]) {
        homeNameObj[homeTeamState[key].name] = true
      } else {
        toast.error(
          `You have entered duplicate players on ${homeTeam.name}. If there are not ${num} unique players on this team, the roster must be edited on the team's page before reporting this game.`,
          { containerId: 'temporary' },
        )
        return false
      }
    }

    const awayNameObj: { [key: string]: boolean } = {}
    for (let key in awayTeamState) {
      if (!awayNameObj[awayTeamState[key].name]) {
        awayNameObj[awayTeamState[key].name] = true
      } else {
        toast.error(
          `You have entered duplicate players on ${awayTeam.name}. If there are not ${num} unique players on this team, the roster must be edited on the team's page before reporting this game.`,
          { containerId: 'temporary' },
        )
        return false
      }
    }

    if (homeTeamScore === awayTeamScore) {
      toast.error('The score you have entered represents a tie. Please indicate a winner.', {
        containerId: 'temporary',
      })
      return false
    }

    return true
  }

  const handleSubmit = () => {
    const validData = checkData()
    if (validData) {
      const gamePayload = {
        id: game.id,
        homeTeam: {
          score: parseInt(homeTeamScore),
          userSubmittedLineup: Object.values(homeTeamState).map((entry: any) => {
            return { playerId: playerLookupId[entry.name], kills: parseInt(entry.kills) }
          }),
        },
        awayTeam: {
          score: parseInt(awayTeamScore),
          userSubmittedLineup: Object.values(awayTeamState).map((entry: any) => {
            return { playerId: playerLookupId[entry.name], kills: parseInt(entry.kills) }
          }),
        },
      }
      if (!loading) {
        reportFortniteGame({
          variables: {
            gameId: gamePayload.id,
            homeTeam: gamePayload.homeTeam,
            awayTeam: gamePayload.awayTeam,
            winningTeamId: homeTeamScore > awayTeamScore ? homeTeam.id : awayTeam.id,
            evidenceScreenshot: evidenceScreenshotUrl, // is a  Base64 encoded data URL
          },
        })
      }
    }
  }

  return (
    <RowBox>
      {/* top row */}
      <Flex justifyContent="space-between" flexDirection={['column', 'column', 'row']}>
        <Flex alignItems="center" mb={[2, 2, 0]}>
          <Box mr={4}>
            <SportLogo sport={homeTeam.sportSlug} width="2rem" height="2rem" />
          </Box>
          <Text color="darkgray">
            <h3>Game {numToString(index + 1)}</h3>
          </Text>
          {game.winner && game.winner.team && (
            <Text color="green" ml={5} mt={'2px'}>
              <h5>{game.winner.team.truncatedName}</h5>
            </Text>
          )}
        </Flex>

        <Flex
          alignItems={['flex-start', 'flex-start', 'center']}
          width={[1, 1, 1 / 2]}
          justifyContent="flex-end"
          flexDirection={['column', 'column', 'row']}
        >
          {loading ? (
            <LoadingSpinner />
          ) : report !== index ? (
            <Flex mr={5} mb={[2, 2, 0]} alignItems="center">
              <Circle bg={game.winner ? 'green' : !settled ? 'red' : 'middlegray'} mr={2} />
              <Text color="darkgray" mt={'2px'}>
                <h6>
                  {game.winner
                    ? 'game reported'
                    : reportable
                    ? 'game unreported'
                    : settled
                    ? 'game not needed'
                    : 'report previous game'}
                </h6>
              </Text>
            </Flex>
          ) : null}
          {((!game.winner && !loading && reportable && !settled) || currentUserIsAdmin()) && (
            <BaseButton
              variant={activeBox && !ready ? 'primaryDisabled' : 'primary'}
              disabled={(activeBox && !ready) || loading}
              onClick={activeBox ? handleSubmit : () => changeOpenIndex(index)}
            >
              {activeBox ? 'submit game report' : 'report game'}
            </BaseButton>
          )}
        </Flex>
      </Flex>
      {/* reporting content */}
      {activeBox && (
        <Reporting mt={5} flexWrap="wrap" mx={[0, 0, 0, -2]} p={1}>
          <FortniteReportingTable
            isHomeTeam={true}
            team={homeTeam}
            changeName={handleNameChange}
            changeKills={handleKillsChange}
            teamResults={homeTeamState}
          />

          <FortniteReportingTable
            isHomeTeam={false}
            team={awayTeam}
            changeName={handleNameChange}
            changeKills={handleKillsChange}
            teamResults={awayTeamState}
          />

          <GameBoxReportEvidence
            imageName={evidenceScreenshotName}
            imageUrl={evidenceScreenshotUrl}
            onImageChanged={handleEvidenceChanged}
          />
        </Reporting>
      )}
    </RowBox>
  )
}

export default FortniteGameBox
